import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Briefcase, Workflow, ListChecks } from "lucide-react";
import { ROLES, workflowsForRole, findWorkflow, DEMO_EMAIL_BY_ROLE } from "@/config/wizard-workflows";

const STAGE = { ROLE: 1, WORKFLOW: 2, STEPS: 3 };

export default function LoginWizard() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState(STAGE.ROLE);
  const [role, setRole] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(null);

  const workflow = role && workflowId ? findWorkflow(role, workflowId) : null;

  const reset = () => {
    setStage(STAGE.ROLE);
    setRole(null);
    setWorkflowId(null);
  };

  const goStep = async (route) => {
    if (!role) return;
    const email = DEMO_EMAIL_BY_ROLE[role];
    if (!email) {
      toast.error(`Không có tài khoản demo cho vai trò ${role}`);
      return;
    }
    setLoadingRoute(route);
    try {
      const u = await login(email, "password123");
      toast.success(`Đăng nhập ${u.full_name} — mở ${route}`);
      nav(route);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Đăng nhập thất bại");
    } finally {
      setLoadingRoute(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="login-wizard">
      <WizardHeader stage={stage} role={role} workflow={workflow} onBack={() => {
        if (stage === STAGE.STEPS) setStage(STAGE.WORKFLOW);
        else if (stage === STAGE.WORKFLOW) { setWorkflowId(null); setStage(STAGE.ROLE); setRole(null); }
      }} onReset={reset} />

      {stage === STAGE.ROLE && (
        <RoleGrid onPick={(r) => { setRole(r); setStage(STAGE.WORKFLOW); }} />
      )}

      {stage === STAGE.WORKFLOW && role && (
        <WorkflowGrid role={role} onPick={(id) => { setWorkflowId(id); setStage(STAGE.STEPS); }} />
      )}

      {stage === STAGE.STEPS && workflow && (
        <StepList workflow={workflow} onGo={goStep} loadingRoute={loadingRoute} />
      )}
    </div>
  );
}

function WizardHeader({ stage, role, workflow, onBack, onReset }) {
  const items = [
    { n: 1, label: "Bạn là ai", active: stage >= STAGE.ROLE, icon: Briefcase },
    { n: 2, label: "Chọn workflow", active: stage >= STAGE.WORKFLOW, icon: Workflow },
    { n: 3, label: "Thực hiện", active: stage >= STAGE.STEPS, icon: ListChecks },
  ];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[11px]">
        {items.map((it, idx) => (
          <div key={it.n} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border ${stage === it.n ? "bg-emerald-800 border-emerald-800 text-white" : it.active ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-stone-100 border-stone-200 text-stone-500"}`}>
              <it.icon className="w-3 h-3" />
              <span className="font-semibold">{it.n}. {it.label}</span>
            </div>
            {idx < items.length - 1 && <ChevronRight className="w-3 h-3 text-stone-300" />}
          </div>
        ))}
      </div>
      {stage > STAGE.ROLE && (
        <div className="flex items-center justify-between text-xs">
          <button onClick={onBack} className="flex items-center gap-1 text-stone-600 hover:text-emerald-900 font-semibold" data-testid="wizard-back">
            <ChevronLeft className="w-3 h-3" /> Quay lại
          </button>
          <button onClick={onReset} className="text-stone-400 hover:text-stone-700 underline" data-testid="wizard-reset">
            Bắt đầu lại
          </button>
        </div>
      )}
      {role && (
        <div className="text-[11px] text-stone-500">
          Vai trò: <span className="font-bold text-emerald-900">{ROLES.find((r) => r.role === role)?.label}</span>
          {workflow && <> • Workflow: <span className="font-bold text-emerald-900">{workflow.title}</span></>}
        </div>
      )}
    </div>
  );
}

function RoleGrid({ onPick }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="wizard-roles">
      {ROLES.map((r) => (
        <button
          key={r.role}
          type="button"
          onClick={() => onPick(r.role)}
          className="text-left p-3 rounded-sm bg-white border border-stone-200 hover:border-emerald-700 hover:bg-emerald-50 transition-colors group"
          data-testid={`wizard-role-${r.role}`}
        >
          <div className="text-xs font-bold text-stone-800 group-hover:text-emerald-900">{r.label}</div>
          <div className="text-[10px] text-stone-500 mt-0.5 leading-tight">{r.desc}</div>
        </button>
      ))}
    </div>
  );
}

function WorkflowGrid({ role, onPick }) {
  const list = workflowsForRole(role);
  if (!list.length) {
    return <div className="text-xs text-stone-500 italic p-4 text-center">Chưa có workflow demo cho vai trò này.</div>;
  }
  return (
    <div className="space-y-2" data-testid="wizard-workflows">
      {list.map((w) => (
        <button
          key={w.id}
          type="button"
          onClick={() => onPick(w.id)}
          className="w-full text-left p-3 rounded-sm bg-white border border-stone-200 hover:border-emerald-700 hover:bg-emerald-50 transition-colors group"
          data-testid={`wizard-workflow-${w.id}`}
        >
          <div className="text-sm font-bold text-stone-800 group-hover:text-emerald-900">{w.title}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">{w.desc}</div>
          <div className="text-[10px] text-stone-400 mt-1.5 font-mono">{w.steps.length} bước</div>
        </button>
      ))}
    </div>
  );
}

function StepList({ workflow, onGo, loadingRoute }) {
  return (
    <ol className="space-y-1.5" data-testid="wizard-steps">
      {workflow.steps.map((s, i) => {
        const isLoading = loadingRoute === s.route;
        return (
          <li key={i}>
            <Button
              type="button"
              variant="outline"
              onClick={() => onGo(s.route)}
              disabled={!!loadingRoute}
              className="w-full justify-start h-auto py-2.5 rounded-sm border-stone-200 hover:border-emerald-700 hover:bg-emerald-50"
              data-testid={`wizard-step-${i + 1}`}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-xs font-semibold text-stone-800">{s.label}</div>
                  <div className="text-[10px] text-stone-400 font-mono">{s.route}</div>
                </div>
                {isLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-700 shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                )}
              </div>
            </Button>
          </li>
        );
      })}
    </ol>
  );
}
